import { SafeHtml } from '@angular/platform-browser';
import { ChatMessageDTO } from './chatmessage.modal';
import { Profile } from './profile.model';
import { EventViewingState } from './event-viewing.model';

export type EventAudiencePolicy = 'PUBLIC' | 'JURISDICTION';
export type EventInvitationPolicy =
  | 'NONE'
  | 'ALL_SUBSCRIBERS'
  | 'JURISDICTION_SUBSCRIBERS';
export type EventRsvpPolicy = 'ENABLED' | 'DISABLED';
export type EventChatPolicy = 'VERIFIED_IDENTITY' | 'DISABLED';

export class AttendeeDTO {
  name: string;
  email: string;
  joinTime: Date;
  leaveTime: Date;
  verified: boolean;
  rsvp: boolean | null;
  emailConsented: boolean;
  minutesAttended: number;
  phoneNumber: string | null;

  constructor() {
    this.name = '';
    this.email = '';
    this.joinTime = new Date();
    this.leaveTime = new Date();
    this.verified = false;
    this.rsvp = null;
    this.emailConsented = false;
    this.minutesAttended = 0;
    this.phoneNumber = null;
  }
}

export class EventsDTO {
  id: number | null;
  eventName: string;
  agenda: string;
  byUserId: number;
  byProfileId: number;
  byProfileName: string;
  byProfileTagName: string;
  byProfileImage: string;
  timezone: string;
  dateOfEvent: Date;
  startTime: string;
  endTime: string;
  sendInviteToAll: boolean;
  audiencePolicy: EventAudiencePolicy;
  invitationPolicy: EventInvitationPolicy;
  rsvpPolicy: EventRsvpPolicy;
  chatPolicy: EventChatPolicy;
  allowLateRsvp: boolean;
  isPublish: boolean;
  city: string;
  state: string;
  country: string;
  eventStatus: string;
  safeAgenda?: SafeHtml;
  roomId: string;
  webinarId: string;
  streamId: string;
  publicName: string;
  recordingUrls: Array<string>;
  todaToken: string;
  chatMessages: Array<ChatMessageDTO>;
  chatCount: number;
  rsvpCount: number;
  rsvpProfiles: Array<Profile>;
  isRsvp: boolean;
  isHost: boolean;
  viewingState: EventViewingState | null;
  canRsvp: boolean;
  totalAttendees: number | null;
  verifiedAttendees: number | null;
  unverifiedAttendees: number | null;
  verifiedConstituentsPercentage: number | null;
  averageTimeWatched: string | null;
  attendees: Array<AttendeeDTO>;
  aiChatSummary: string;
  claimedStatus: string | null;
  webinarPasscode: string;

  static fromResponse(data: Partial<EventsDTO> | null | undefined): EventsDTO {
    const event = Object.assign(new EventsDTO(), data ?? {});
    event.rsvpCount =
      typeof event.rsvpCount === 'number' && Number.isFinite(event.rsvpCount)
        ? Math.max(event.rsvpCount, 0)
        : 0;
    event.rsvpProfiles = Array.isArray(event.rsvpProfiles)
      ? event.rsvpProfiles
      : [];
    return event;
  }

  applyRsvpResult(
    action: 'RSVP' | 'CANCEL',
    result:
      | 'added'
      | 'already_added'
      | 'removed'
      | 'already_removed'
      | {
          action?: 'added' | 'already_added' | 'removed' | 'already_removed';
          isRsvp?: boolean;
          rsvpCount?: number;
        }
  ): void {
    if (result && typeof result === 'object') {
      if (typeof result.isRsvp === 'boolean') {
        this.isRsvp = result.isRsvp;
      }
      if (
        typeof result.rsvpCount === 'number' &&
        Number.isFinite(result.rsvpCount)
      ) {
        this.rsvpCount = Math.max(result.rsvpCount, 0);
      }
      return;
    }

    const currentCount =
      typeof this.rsvpCount === 'number' && Number.isFinite(this.rsvpCount)
        ? Math.max(this.rsvpCount, 0)
        : 0;

    if (action === 'RSVP') {
      this.isRsvp = true;
      this.rsvpCount = result === 'added' ? currentCount + 1 : currentCount;
      return;
    }

    this.isRsvp = false;
    this.rsvpCount =
      result === 'removed' ? Math.max(currentCount - 1, 0) : currentCount;
  }

  constructor() {
    this.id = null;
    this.eventName = '';
    this.agenda = '';
    this.byUserId = 0;
    this.byProfileId = 0;
    this.byProfileName = '';
    this.byProfileTagName = '';
    this.byProfileImage = '';
    this.timezone = 'Pacific Time';
    this.dateOfEvent = new Date();
    this.startTime = '';
    this.endTime = '';
    this.sendInviteToAll = true;
    this.audiencePolicy = 'PUBLIC';
    this.invitationPolicy = 'ALL_SUBSCRIBERS';
    this.rsvpPolicy = 'ENABLED';
    this.chatPolicy = 'VERIFIED_IDENTITY';
    this.allowLateRsvp = false;
    this.isPublish = false;
    this.city = '';
    this.state = '';
    this.country = '';
    this.eventStatus = '';
    this.roomId = '';
    this.webinarId = '';
    this.streamId = '';
    this.publicName = '';
    this.recordingUrls = new Array();
    this.todaToken = '';
    this.chatMessages = new Array();
    this.chatCount = 0;
    this.rsvpCount = 0;
    this.rsvpProfiles = [];
	this.isRsvp = false;
	this.isHost = false;
	this.viewingState = null;
	this.canRsvp = false;
    this.totalAttendees = 0;
    this.verifiedAttendees = 0;
    this.unverifiedAttendees = 0;
    this.verifiedConstituentsPercentage = 0;
    this.averageTimeWatched = null;
    this.attendees = new Array();
    this.aiChatSummary = '';
    this.claimedStatus = null;
    this.webinarPasscode = '';
  }
}
